<template>
    <div id="forms-page">
        <loader v-if="is('loading')"></loader>

        <div class="forms-card" v-if="!is('loading')">
            <header class="forms-card-header">
                <div class="forms-heading">
                    <h1>{{ t('forms-manage_title') }}</h1>
                    <span class="forms-count" v-if="forms.length">
                        {{ t('forms-count_forms', [forms.length]) }}
                    </span>
                </div>
                <div class="forms-header-actions">
                    <kv-input
                        class="forms-search"
                        size="small"
                        variant="outlined"
                        prepend-icon="ui-search-01"
                        v-model="search"
                        :placeholder="t('forms-search_placeholder')"
                    />
                    <kv-button
                        variant="filled"
                        size="small"
                        prepend-icon="ui-plus"
                        :disabled="reachedFormsLimit"
                        @click="openForm()"
                    >
                        {{ t('forms-new_btn') }}
                    </kv-button>
                </div>
            </header>

            <div class="forms-limit-warning" v-if="reachedFormsLimit">
                <Icon icon-class="ui-alert-triangle" size="3" />
                <span>{{ t('forms-max_reached', [$module('forms').max]) }}</span>
            </div>

            <div class="forms-card-body">
                <div class="forms-empty" v-if="!visibleForms.length">
                    <Icon icon-class="ui-file-02" size="12" />
                    <p v-if="search">{{ t('forms-empty_search') }}</p>
                    <template v-else>
                        <p class="forms-empty-title">{{ t('forms-empty_title') }}</p>
                        <p>{{ t('forms-empty_subtitle') }}</p>
                    </template>
                </div>

                <div class="forms-list" v-else>
                    <article
                        class="form-row"
                        v-for="form in visibleForms"
                        :key="form.id"
                    >
                        <router-link class="form-row-main" :to="`/forms/${form.id}`">
                            <span class="form-row-icon">
                                <Icon icon-class="ui-file-02" size="5" />
                            </span>
                            <span class="form-row-text">
                                <!-- i18n-validator-ignore-next-line: runtime form name -->
                                <span class="form-row-name">{{ form.name }}</span>
                                <span class="form-row-meta">
                                    <span>{{ t('forms-questions_count', [countQuestions(form)]) }}</span>
                                    <span class="form-row-dot"></span>
                                    <span>{{ t('forms-answers_count', [form.totalAnswers || 0]) }}</span>
                                </span>
                            </span>
                        </router-link>

                        <div class="form-row-actions">
                            <kv-button
                                variant="outlined"
                                size="small"
                                prepend-icon="ui-message-question-square"
                                :to="`/forms/${form.id}`"
                            >
                                {{ t('forms-action_questions') }}
                            </kv-button>
                            <kv-button
                                variant="outlined"
                                size="small"
                                prepend-icon="ui-message-text-square-01"
                                :to="answersRoute(form.id)"
                            >
                                {{ t('forms-action_answers') }}
                            </kv-button>

                            <kv-popup placement="bottom left" min-width="220px">
                                <template #trigger="{ toggle }">
                                    <kv-button
                                        variant="text"
                                        size="small"
                                        prepend-icon="ui-dots-vertical"
                                        :aria-label="t('forms-more_actions')"
                                        @click="toggle"
                                    />
                                </template>
                                <template #content="{ toggle }">
                                    <div class="forms-menu">
                                        <a
                                            class="forms-menu-item"
                                            :href="formUrl(form.id)"
                                            target="_blank"
                                            @click="toggle"
                                        >
                                            <Icon icon-class="ui-link-01" size="3" />
                                            {{ t('forms-tooltip_view_tab') }}
                                        </a>
                                        <button
                                            class="forms-menu-item"
                                            type="button"
                                            @click="copyLink(form.id); toggle()"
                                        >
                                            <Icon icon-class="ui-copy-03" size="3" />
                                            {{ t('forms-action_copy_link') }}
                                        </button>
                                        <router-link
                                            class="forms-menu-item"
                                            v-if="canSeeReports"
                                            :to="reportRoute(form.id)"
                                            @click.native="toggle"
                                        >
                                            <Icon icon-class="ui-bar-chart-07" size="3" />
                                            {{ t('forms-tooltip_report') }}
                                        </router-link>
                                        <button
                                            class="forms-menu-item"
                                            type="button"
                                            @click="exportAnswers(form.id); toggle()"
                                        >
                                            <Icon icon-class="ui-download-01" size="3" />
                                            {{ t('forms-tooltip_export') }}
                                        </button>
                                        <button
                                            class="forms-menu-item"
                                            type="button"
                                            @click="openForm(form); toggle()"
                                        >
                                            <Icon icon-class="ui-edit-05" size="3" />
                                            {{ t('forms-tooltip_rename') }}
                                        </button>
                                        <button
                                            class="forms-menu-item"
                                            type="button"
                                            @click="askDuplicate(form); toggle()"
                                        >
                                            <Icon icon-class="ui-copy-03" size="3" />
                                            {{ t('forms-tooltip_duplicate') }}
                                        </button>
                                        <button
                                            class="forms-menu-item forms-menu-item--danger"
                                            type="button"
                                            @click="askRemove(form); toggle()"
                                        >
                                            <Icon icon-class="ui-trash-01" size="3" />
                                            {{ t('forms-tooltip_remove') }}
                                        </button>
                                    </div>
                                </template>
                            </kv-popup>
                        </div>
                    </article>
                </div>
            </div>
        </div>

        <modal id="forms-modal" size="sm" ref="forms-modal">
            <template slot="title">
                {{
                    form.id
                        ? t('forms-modal_rename_title')
                        : t('forms-modal_add_title')
                }}
            </template>
            <template slot="body">
                <kv-input
                    id="formName"
                    variant="outlined"
                    :label="t('forms-name_label')"
                    v-model="form.name"
                    @keydown="onNameKeydown"
                />
                <p class="forms-field-error" v-if="validationsErrors.name">
                    {{ validationsErrors.name }}
                </p>
            </template>
            <template slot="footer">
                <kv-button variant="outlined" @click="closeForm">
                    {{ t('forms-btn_cancel') }}
                </kv-button>
                <kv-button
                    variant="filled"
                    :prepend-icon="form.id ? 'ui-save-01' : 'ui-plus'"
                    :disabled="!isValidName"
                    @click="saveForm"
                >
                    {{ form.id ? t('forms-btn_save') : t('forms-btn_add') }}
                </kv-button>
            </template>
        </modal>

        <confirm id="confirmRemoveForm" ref="confirmRemoveForm" @confirm="remove">
            <template slot="title">
                <span>
                    {{ t('forms-confirm_delete_question') }}
                    <!-- i18n-validator-ignore-next-line: runtime form name -->
                    <b>"{{ pendingForm ? pendingForm.name : '' }}"</b>?
                </span>
            </template>
            <template slot="body">
                <div>{{ t('forms-confirm_delete_body') }}</div>
            </template>
        </confirm>

        <confirm id="confirmDuplicateForm" ref="confirmDuplicateForm" @confirm="duplicateForm">
            <template slot="title">
                <span>
                    {{ t('forms-confirm_duplicate_question') }}
                    <!-- i18n-validator-ignore-next-line: runtime form name -->
                    <b>"{{ pendingForm ? pendingForm.name : '' }}"</b>?
                </span>
            </template>
            <template slot="body">
                <div>{{ t('forms-confirm_duplicate_body') }}</div>
            </template>
        </confirm>
    </div>
</template>
<script>
import keviApi from "../keviApi";
import Vue from "vue";
import saveData from "../helpers/saveData";
import Icon from "../components/icon/icon";
import KvButton from "../components/form/kv-button.vue";
import KvInput from "../components/inputs/kv-input.vue";
import KvPopup from "../components/ui/kv-popup.vue";

export default {
    props: {
        user: Object,
    },
    data: function () {
        return {
            state: "loading",
            forms: [],
            formsDefault: [],
            newCount: 0,
            search: "",
            pendingForm: null,
            form: {
                id: null,
                name: null,
                new: false,
                active: 1,
            },
            validationsErrors: {
                name: null,
            },
        };
    },
    components: {
        Icon,
        "kv-button": KvButton,
        "kv-input": KvInput,
        "kv-popup": KvPopup,
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
        isValidName() {
            return this.form.name && this.form.name.length >= 2;
        },
        canSeeReports() {
            // Read the permission off the state directly: dispatch() returns a
            // Promise, which is always truthy and would show the link to
            // everyone.
            const user = this.$store.state.user;
            return !!(user && user.id && user.permissions?.reports?.enabled);
        },
        visibleForms() {
            const term = this.search.trim().toLowerCase();
            if (!term) {
                return this.forms;
            }
            return this.forms.filter((form) =>
                (form.name || "").toLowerCase().includes(term),
            );
        },
        reachedFormsLimit() {
            return this.$module(
                'forms',
                (o) => o.max && o.max <= this.forms.length,
            );
        },
    },
    mounted: function () {
        document.getElementsByTagName("title")[0].innerText =
            this.t('forms-page_title');
        this.loadForms();
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        formUrl: function (id) {
            return window.baseUrl + "#/form/" + this.place.slug + "/" + id;
        },
        answersRoute(formId) {
            return {
                path: "/inbox",
                query: { filter: JSON.stringify({ forms: formId }) },
            };
        },
        reportRoute(formId) {
            return {
                path: "/report",
                query: { filter: JSON.stringify({ forms: formId }) },
            };
        },
        countQuestions(form) {
            return (form.questions || []).filter((q) => q.active != 0).length;
        },
        copyLink(formId) {
            const url = this.formUrl(formId);
            const done = () =>
                this.$store.dispatch(
                    "notification/success",
                    this.t('forms-link_copied'),
                );

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(url).then(done);
                return;
            }
            const helper = document.createElement("textarea");
            helper.value = url;
            document.body.appendChild(helper);
            helper.select();
            document.execCommand("copy");
            document.body.removeChild(helper);
            done();
        },
        resetForm() {
            this.form = {
                id: null,
                name: null,
                new: true,
                active: true,
            };
        },
        exportAnswers(formId) {
            let data = {
                filters: {
                    forms: formId,
                },
            };
            let requestOptions = keviApi.getOptions(
                "inbox/export",
                "GET",
                data,
            );
            Vue.http(requestOptions).then((response) => {
                if (response.status === 200) {
                    if (response.body) {
                        window.mixpanel?.track("Export form questions");
                        saveData(response.body, "respostas.csv");
                    }
                }
            });
        },
        openForm: async function (form = null) {
            let formsModal = await this.$refs["forms-modal"];
            formsModal.open();

            if (form) {
                form.new = false;
                this.form = JSON.parse(JSON.stringify(form));
            } else {
                this.resetForm();
            }
        },
        closeForm: function () {
            this.$refs["forms-modal"].close();
            this.resetForm();
        },
        generateId: function () {
            this.newCount = this.newCount + 1;
            return "new-" + this.newCount;
        },
        addForm: function (form) {
            form = JSON.parse(JSON.stringify(form));
            form.active = true;
            form.id = this.generateId();
            form.new = true;
            this.forms.push(form);
            this.save();
            this.closeForm();
        },
        updateForm: function (form) {
            form = JSON.parse(JSON.stringify(form));
            form.new = false;
            form.active = true;
            let key = this.forms.findIndex((f) => f.id === form.id);
            if (key !== -1) {
                this.forms.splice(key, 1, form);
            }
            this.save();
            this.closeForm();
        },
        askDuplicate(form) {
            this.pendingForm = form;
            this.$refs.confirmDuplicateForm.open();
        },
        askRemove(form) {
            this.pendingForm = form;
            this.$refs.confirmRemoveForm.open();
        },
        duplicateForm() {
            if (!this.pendingForm) {
                return;
            }
            let options = keviApi.getOptions(
                `form/${this.pendingForm.id}/duplicate`,
                "POST",
            );
            this.pendingForm = null;
            this.save(false);
            this.$http(options).then(() => {
                this.$store.dispatch(
                    "notification/success",
                    this.t('forms-notification_duplicated'),
                );
                this.loadForms();
            });
        },
        remove: function () {
            if (!this.pendingForm) {
                return;
            }
            const index = this.forms.findIndex(
                (f) => f.id === this.pendingForm.id,
            );
            this.pendingForm = null;
            if (index === -1) {
                return;
            }
            if (this.forms[index].new) {
                this.forms.splice(index, 1);
            } else {
                this.forms[index].active = false;
            }
            this.save(false);
            this.$store.dispatch(
                "notification/success",
                this.t('forms-notification_deleted'),
            );
        },
        onNameKeydown(event) {
            if (event.key === "Enter") {
                event.preventDefault();
                this.saveForm();
            }
        },
        saveForm: function () {
            if (this.isValidName) {
                if (!this.form.id) {
                    return this.addForm(this.form);
                }
                this.updateForm(this.form);
            }
        },
        save: function (dispatch = true) {
            this.setState("saving");
            var options = keviApi.getOptions("forms", "POST", {
                forms: this.forms,
            });
            this.$http(options).then(
                function (response) {
                    this.setState("ready");
                    if (response.status === 200) {
                        if (dispatch) {
                            this.$store.dispatch(
                                "notification/success",
                                this.t('forms-notification_saved'),
                            );
                        }
                        this.loadForms();
                    }
                },
                () => {
                    this.setState("ready");
                },
            );
        },
        loadForms: function () {
            this.setState("loading");
            var options = keviApi.getOptions("forms");
            this.$http(options).then(
                function (response) {
                    this.setState("ready");
                    if (response.status === 200) {
                        var forms = response.body;
                        for (var q in forms) {
                            forms[q].active = true;
                            forms[q].new = false;
                        }
                        this.formsDefault = forms;
                        return;
                    }
                    this.formsDefault = [];
                },
                () => {
                    this.setState("ready");
                    this.formsDefault = [];
                },
            );
        },
    },
    watch: {
        formsDefault: function () {
            this.forms = JSON.parse(JSON.stringify(this.formsDefault));
        },
    },
};
</script>
<style scoped lang="less">
#forms-page {
    padding: 20px;

    @media screen and (max-width: 700px) {
        padding: 12px;
    }
}

.forms-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
}

.forms-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px 12px;
    flex-wrap: wrap;

    h1 {
        margin: 0;
        font-size: 20px;
        font-weight: 500;
        color: #111927;
    }

    @media screen and (max-width: 767px) {
        padding: 16px 16px 12px;
    }
}

.forms-heading {
    display: flex;
    align-items: baseline;
    gap: 10px;
    min-width: 0;
}

.forms-count {
    font-size: 13px;
    color: #6c737f;
}

.forms-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .forms-search {
        width: 220px;

        @media screen and (max-width: 767px) {
            display: none;
        }
    }
}

.forms-limit-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 24px 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fffaeb;
    color: #b54708;
    font-size: 13px;

    @media screen and (max-width: 767px) {
        margin: 0 16px 12px;
    }
}

.forms-card-body {
    border-top: 1px solid #f3f4f6;
}

.forms-list {
    display: flex;
    flex-direction: column;
}

.form-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 24px;
    border-bottom: 1px solid #f3f4f6;
    transition: background-color 0.15s ease;

    &:last-child {
        border-bottom: 0;
    }

    &:hover {
        background: #f9fafb;
    }

    @media screen and (max-width: 767px) {
        padding: 12px 16px;
        flex-wrap: wrap;
    }
}

.form-row-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1 1 auto;
    text-decoration: none;
    color: inherit;

    &:hover .form-row-name {
        color: #1d7836;
    }
}

.form-row-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #f3f4f6;
    color: #1f2a37;
    flex-shrink: 0;
}

.form-row-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.form-row-name {
    font-size: 14px;
    font-weight: 500;
    color: #111927;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.form-row-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #6c737f;
}

.form-row-dot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #d1d5db;
}

.form-row-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.forms-menu {
    display: flex;
    flex-direction: column;
    padding: 4px;

    .forms-menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #1f2a37;
        font-size: 13px;
        text-align: left;
        text-decoration: none;
        white-space: nowrap;

        &:hover {
            background: #f3f4f6;
        }

        &--danger {
            color: #dc2626;
        }
    }
}

.forms-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 56px 24px;
    color: #6c737f;
    text-align: center;

    p {
        margin: 0;
        font-size: 13px;
    }

    .forms-empty-title {
        font-size: 15px;
        font-weight: 500;
        color: #111927;
    }
}

.forms-field-error {
    margin: 8px 0 0;
    font-size: 12px;
    color: #b91c1c;
}
</style>
